import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, History, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import { SubscribersTab } from "@/components/subscribers-tab";
import { PlanVersionsDialog } from "@/components/plan-versions-dialog";
import {
  TreatmentPicker,
  type PickedTreatment,
} from "@/components/treatment-picker";

export const Route = createFileRoute("/_authenticated/subscriptions")({
  component: SubscriptionsPage,
});

const zl = (grosze: number) =>
  `${(grosze / 100).toFixed(2).replace(".", ",")} zł`;

const toGrosze = (zloty: string) =>
  Math.round(parseFloat((zloty || "0").replace(",", ".")) * 100);
const toZloty = (grosze: number) => (grosze / 100).toFixed(2);

const STATUS_LABEL = {
  draft: "Szkic",
  active: "Opublikowany",
  archived: "Zarchiwizowany",
} as const;

const INTERVAL_LABEL = {
  month: "miesięcznie",
  quarter: "kwartalnie",
  year: "rocznie",
  once: "jednorazowo",
} as const;

/**
 * Rollover and commitment are counted in billing cycles, so they are named
 * after the plan's cycle. Plans made here are monthly; the others can only
 * come from the API, and a one-off plan has no cycles to speak of.
 */
const CYCLE_UNIT = {
  month: { plural: "miesiące", short: "mies.", end: "miesiąca" },
  quarter: { plural: "kwartały", short: "kw.", end: "kwartału" },
  year: { plural: "lata", short: "l.", end: "roku" },
  once: { plural: "miesiące", short: "mies.", end: "miesiąca" },
} as const;

/**
 * Every treatment, not just the first page — a salon past 100 treatments
 * could otherwise never pick, or even label, anything beyond that page.
 */
function useAllTreatments() {
  const first = trpc.treatment.list.useQuery({ page: 1, limit: 100 });
  const totalPages = first.data?.totalPages ?? 1;
  const extraPages = Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => i + 2);

  const rest = trpc.useQueries((t) =>
    extraPages.map((page) => t.treatment.list({ page, limit: 100 })),
  );

  return {
    items: [
      ...(first.data?.items ?? []),
      ...rest.flatMap((q) => q.data?.items ?? []),
    ],
    isLoading: first.isLoading || rest.some((q) => q.isLoading),
  };
}

function SubscriptionsPage() {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  const [tab, setTab] = useState("subscribers");

  // The feature flag doing its job: nothing here is reachable until the salon
  // has deliberately turned memberships on in its settings.
  if (salon && !salon.subscriptionsEnabled) {
    return (
      <Card className="p-6">
        <h1 className="mb-2 text-2xl font-bold">Abonamenty</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Abonamenty są wyłączone dla tego salonu. Włącz je w ustawieniach, żeby
          skonfigurować plany i udostępnić je klientkom w aplikacji.
        </p>
        <Button asChild size="sm">
          <Link to="/settings">Przejdź do ustawień</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Abonamenty</h1>
        <p className="text-sm text-muted-foreground">
          Klientka płaci co okres i dostaje pulę świadczeń
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="subscribers">Klientki</TabsTrigger>
          <TabsTrigger value="plans">Plany</TabsTrigger>
        </TabsList>
        <TabsContent value="subscribers" className="mt-4">
          <SubscribersTab />
        </TabsContent>
        <TabsContent value="plans" className="mt-4">
          <PlansTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlansTab() {
  const { data: plans, isLoading } = trpc.subscription.listPlans.useQuery();
  const utils = trpc.useUtils();

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [entitlementForPlan, setEntitlementForPlan] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string } | null>(null);

  const setStatus = trpc.subscription.setPlanStatus.useMutation({
    onSuccess: () => {
      utils.subscription.listPlans.invalidate();
      toast.success("Zapisano status planu");
    },
    onError: (err) => toast.error(err.message),
  });

  const deletePlan = trpc.subscription.deletePlan.useMutation({
    onSuccess: () => {
      utils.subscription.listPlans.invalidate();
      toast.success("Plan usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  const removeEntitlement = trpc.subscription.removeEntitlement.useMutation({
    onSuccess: () => {
      utils.subscription.listPlans.invalidate();
      toast.success("Świadczenie usunięte");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {plans?.length ?? 0} planów
        </p>
        <Button
          size="sm"
          onClick={() => {
            setEditingPlanId(null);
            setPlanDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Dodaj plan
        </Button>
      </div>

      {plans?.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">
            Nie masz jeszcze żadnego planu. Zacznij od jednego — cena, jeden zabieg
            w abonamencie i rabat na resztę oferty wystarczą, żeby zacząć sprzedawać.
          </p>
        </Card>
      )}

      {plans?.map((plan) => (
        <Card key={plan.id} className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{plan.name}</h2>
                <Badge
                  variant={plan.status === "active" ? "default" : "secondary"}
                >
                  {STATUS_LABEL[plan.status]}
                </Badge>
                {plan.badgeLabel && (
                  <Badge variant="outline">{plan.badgeLabel}</Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {zl(plan.price)} {INTERVAL_LABEL[plan.billingInterval]}
                {plan.discountPercent > 0 &&
                  ` · −${plan.discountPercent}% na pozostałe zabiegi`}
                {plan.minCommitmentCycles > 0 &&
                  ` · zobowiązanie na ${plan.minCommitmentCycles} ${CYCLE_UNIT[plan.billingInterval].short}`}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Aktywnych subskrypcji: {plan.activeSubscribers} · wersja{" "}
                {plan.version} ·{" "}
                {plan.creditsExpireAfterCycles > 0
                  ? `niewykorzystane zabiegi przechodzą na ${plan.creditsExpireAfterCycles} kolejne ${CYCLE_UNIT[plan.billingInterval].short}`
                  : `niewykorzystane zabiegi przepadają z końcem ${CYCLE_UNIT[plan.billingInterval].end}`}
              </p>
            </div>

            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="ghost"
                title="Historia wersji: jakie warunki obowiązywały kiedy i kto je zmienił"
                onClick={() => setHistoryFor({ id: plan.id, name: plan.name })}
              >
                <History className="mr-1 size-4" />
                Historia
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingPlanId(plan.id);
                  setPlanDialogOpen(true);
                }}
              >
                Edytuj
              </Button>
              {plan.status === "active" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setStatus.isPending}
                  onClick={() =>
                    setStatus.mutate({ planId: plan.id, status: "archived" })
                  }
                >
                  Zarchiwizuj
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={setStatus.isPending}
                  onClick={() =>
                    setStatus.mutate({ planId: plan.id, status: "active" })
                  }
                >
                  Opublikuj
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={deletePlan.isPending || plan.activeSubscribers > 0}
                title={
                  plan.activeSubscribers > 0
                    ? "Plan ma subskrybentów — możesz go zarchiwizować, ale nie usunąć"
                    : undefined
                }
                onClick={() => deletePlan.mutate({ planId: plan.id })}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>

          <PlanWarnings planId={plan.id} />

          <div className="mt-4 border-t pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium">Co zawiera abonament</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEntitlementForPlan(plan.id)}
              >
                <Plus className="mr-1 size-3" />
                Dodaj świadczenie
              </Button>
            </div>

            {plan.entitlements.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Brak świadczeń — planu nie da się opublikować, dopóki nie dodasz
                przynajmniej jednego.
              </p>
            ) : (
              <ul className="space-y-2">
                {plan.entitlements.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between rounded border px-3 py-2 text-sm"
                  >
                    <EntitlementLabel entitlement={e} />
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={removeEntitlement.isPending}
                      onClick={() =>
                        removeEntitlement.mutate({ entitlementId: e.id })
                      }
                    >
                      <Trash2 className="size-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      ))}

      <PlanDialog
        key={`${editingPlanId ?? "new"}-${planDialogOpen}`}
        open={planDialogOpen}
        planId={editingPlanId}
        onClose={() => setPlanDialogOpen(false)}
      />

      <EntitlementDialog
        planId={entitlementForPlan}
        onClose={() => setEntitlementForPlan(null)}
      />

      {historyFor && (
        <PlanVersionsDialog
          planId={historyFor.id}
          planName={historyFor.name}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

/**
 * Configuration warnings. They never block saving — they tell the salon what
 * it is about to sell, at the one moment when a costly mistake is cheap to fix.
 */
function PlanWarnings({ planId }: { planId: string }) {
  const { data: warnings } = trpc.subscription.planWarnings.useQuery({ planId });

  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="mt-4 space-y-2">
      {warnings.map((w, i) => (
        <div
          key={i}
          className="flex gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>{w.message}</span>
        </div>
      ))}
    </div>
  );
}

function EntitlementLabel({
  entitlement,
}: {
  entitlement: {
    treatmentId: string | null;
    tagId: string | null;
    quantityPerCycle: number;
    maxRedemptionValue: number | null;
    addonIncluded: boolean;
  };
}) {
  const { items: treatments } = useAllTreatments();
  const { data: tags } = trpc.tag.list.useQuery();

  const name = entitlement.treatmentId
    ? treatments.find((t) => t.id === entitlement.treatmentId)?.name
    : tags?.find((t) => t.id === entitlement.tagId)?.name;

  return (
    <span>
      {entitlement.quantityPerCycle} ×{" "}
      {entitlement.treatmentId ? (
        <strong>{name ?? "zabieg"}</strong>
      ) : (
        <>
          dowolny zabieg z grupy <strong>{name ?? "grupa"}</strong>
        </>
      )}
      {entitlement.maxRedemptionValue !== null && (
        <span className="text-muted-foreground">
          {" "}
          · abonament pokrywa do {zl(entitlement.maxRedemptionValue)}, resztę
          klientka dopłaca
        </span>
      )}
      {entitlement.addonIncluded && (
        <span className="text-muted-foreground"> · z dodatkami</span>
      )}
    </span>
  );
}

function PlanDialog({
  open,
  planId,
  onClose,
}: {
  open: boolean;
  planId: string | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const { data: plans } = trpc.subscription.listPlans.useQuery();
  const existing = plans?.find((p) => p.id === planId);
  const unit = CYCLE_UNIT[existing?.billingInterval ?? "month"];

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "199.00",
    discountPercent: "0",
    creditsExpireAfterCycles: "2",
    minCommitmentCycles: "0",
    badgeLabel: "",
  });

  // Load the plan being edited the first time the dialog sees it.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  if (open && existing && loadedFor !== existing.id) {
    setLoadedFor(existing.id);
    setForm({
      name: existing.name,
      description: existing.description ?? "",
      price: toZloty(existing.price),
      discountPercent: String(existing.discountPercent),
      creditsExpireAfterCycles: String(existing.creditsExpireAfterCycles),
      minCommitmentCycles: String(existing.minCommitmentCycles),
      badgeLabel: existing.badgeLabel ?? "",
    });
  }
  if (open && !planId && loadedFor !== null) {
    setLoadedFor(null);
    setForm({
      name: "",
      description: "",
      price: "199.00",
      discountPercent: "0",
      creditsExpireAfterCycles: "2",
      minCommitmentCycles: "0",
      badgeLabel: "",
    });
  }

  const onSuccess = () => {
    utils.subscription.listPlans.invalidate();
    toast.success("Zapisano plan");
    onClose();
  };
  const onError = (err: { message: string }) => toast.error(err.message);

  const create = trpc.subscription.createPlan.useMutation({ onSuccess, onError });
  const update = trpc.subscription.updatePlan.useMutation({ onSuccess, onError });

  const submit = () => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price: toGrosze(form.price),
      discountPercent: parseInt(form.discountPercent || "0", 10),
      creditsExpireAfterCycles: parseInt(form.creditsExpireAfterCycles || "0", 10),
      minCommitmentCycles: parseInt(form.minCommitmentCycles || "0", 10),
      badgeLabel: form.badgeLabel.trim() || null,
    };

    if (planId) update.mutate({ planId, ...payload });
    else create.mutate(payload);
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{planId ? "Edytuj plan" : "Nowy plan"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nazwa</Label>
              <Input
                id="name"
                value={form.name}
                placeholder="np. Abonament Pielęgnacja"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="description">Opis dla klientki</Label>
              <Textarea
                id="description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Cena miesięczna (zł)</Label>
                <Input
                  id="price"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="discount">Rabat na resztę oferty (%)</Label>
                <Input
                  id="discount"
                  inputMode="numeric"
                  value={form.discountPercent}
                  onChange={(e) =>
                    setForm({ ...form, discountPercent: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="rollover">
                Niewykorzystane zabiegi przechodzą na kolejne ({unit.plural})
              </Label>
              <Input
                id="rollover"
                inputMode="numeric"
                value={form.creditsExpireAfterCycles}
                onChange={(e) =>
                  setForm({ ...form, creditsExpireAfterCycles: e.target.value })
                }
              />
              <p className="mt-1 text-xs text-muted-foreground">
                0 = niewykorzystany zabieg przepada z końcem {unit.end}. 2 =
                klientka może go wykorzystać jeszcze przez 2 kolejne{" "}
                {unit.plural}.
              </p>
            </div>

            <div>
              <Label htmlFor="commitment">Minimalne zobowiązanie ({unit.plural})</Label>
              <Input
                id="commitment"
                inputMode="numeric"
                value={form.minCommitmentCycles}
                onChange={(e) =>
                  setForm({ ...form, minCommitmentCycles: e.target.value })
                }
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Zostaw <strong>0</strong>, dopóki prawnik nie zaopiniuje klauzuli.
                Umowa na czas określony z konsumentem to najbardziej ryzykowny
                element abonamentu — przy wartości powyżej zera klientka i tak
                zawsze może zrezygnować, ale opłaty lecą do końca zobowiązania.
              </p>
            </div>

            <div>
              <Label htmlFor="badge">Wyróżnik (opcjonalnie)</Label>
              <Input
                id="badge"
                value={form.badgeLabel}
                placeholder="np. Najpopularniejszy"
                onChange={(e) => setForm({ ...form, badgeLabel: e.target.value })}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} disabled={pending || !form.name.trim()}>
            {pending ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Sentinel for the "no group" option; Radix Select rejects an empty value. */
const NO_TAG = "none";

function EntitlementDialog({
  planId,
  onClose,
}: {
  planId: string | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const { data: tags } = trpc.tag.list.useQuery(undefined, { enabled: !!planId });

  // Either a tag (`g:<id>`) or a treatment (`t:<id>`); never both. The picked
  // treatment is kept alongside so its name can be shown after the search
  // list scrolls or filters it out of view.
  const [target, setTarget] = useState("");
  const [pickedTreatment, setPickedTreatment] = useState<PickedTreatment | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [ceiling, setCeiling] = useState("");

  const reset = () => {
    setTarget("");
    setPickedTreatment(null);
    setQuantity("1");
    setCeiling("");
  };

  const pickTreatment = (id: string, treatment: PickedTreatment) => {
    setTarget(`t:${id}`);
    setPickedTreatment(treatment);
  };

  const pickTag = (value: string) => {
    setTarget(value === NO_TAG ? "" : value);
    setPickedTreatment(null);
  };

  const add = trpc.subscription.addEntitlement.useMutation({
    onSuccess: () => {
      utils.subscription.listPlans.invalidate();
      toast.success("Dodano świadczenie");
      reset();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const submit = () => {
    if (!planId || !target) return;

    // `parseInt` would turn "2.5" and "2abc" into a silent 2, and the salon
    // would be selling a different number of benefits than it typed.
    const parsedQuantity = Number(quantity.trim());
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
      toast.error("Liczba sztuk musi być liczbą całkowitą większą od zera");
      return;
    }

    const [kind, id] = target.split(":");

    add.mutate({
      planId,
      treatmentId: kind === "t" ? id : null,
      tagId: kind === "g" ? id : null,
      quantityPerCycle: parsedQuantity,
      maxRedemptionValue: ceiling.trim() ? toGrosze(ceiling) : null,
    });
  };

  const isTag = target.startsWith("g:");
  const isTreatment = target.startsWith("t:");
  const activeTags = tags?.filter((tag) => tag.isActive) ?? [];

  return (
    <Dialog open={!!planId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dodaj świadczenie</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label>Grupa zabiegów</Label>
              <Select value={isTag ? target : NO_TAG} onValueChange={pickTag}>
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz grupę" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TAG}>Bez grupy — konkretny zabieg</SelectItem>
                  {activeTags.map((tag) => (
                    <SelectItem key={tag.id} value={`g:${tag.id}`}>
                      Grupa: {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                Grupa jest atrakcyjniejsza dla klientki i sama się rozszerza, gdy
                otagujesz nowy zabieg. Konkretny zabieg jest łatwiejszy do wyceny.
              </p>
            </div>

            {!isTag && (
              <div className="space-y-2">
                <Label htmlFor="ent-treatment">Konkretny zabieg</Label>
                {isTreatment && pickedTreatment && (
                  <p className="text-sm">
                    Wybrano: <strong>{pickedTreatment.name}</strong> ·{" "}
                    {zl(pickedTreatment.price)}
                  </p>
                )}
                <TreatmentPicker
                  id="ent-treatment"
                  mode="single"
                  selectedIds={isTreatment ? [target.slice(2)] : []}
                  onPick={pickTreatment}
                  enabled={!!planId}
                  listClassName="max-h-48"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="qty">Ile sztuk na okres</Label>
                <Input
                  id="qty"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ceiling">Limit pokrycia (zł)</Label>
                <Input
                  id="ceiling"
                  inputMode="decimal"
                  placeholder="bez limitu"
                  value={ceiling}
                  onChange={(e) => setCeiling(e.target.value)}
                />
              </div>
            </div>

            {isTag && (
              <p className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                Przy grupie warto ustawić limit pokrycia, jeśli ceny zabiegów w niej
                mocno się różnią — inaczej każda klientka wybierze najdroższy, a Ty
                wyceniłaś plan na średniej.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Anuluj
          </Button>
          <Button onClick={submit} disabled={add.isPending || !target}>
            {add.isPending ? "Dodawanie..." : "Dodaj"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
